import {
  EddStatus,
  EddTriggerReason,
  EddDocumentType,
  type EddCaseSummary,
  type EddCaseDetail,
  type EddDocument,
  type EddHistoryEntry,
} from "@/core/compliance/entities/EddCase";

// DTO field names mirror the backend EddCaseResponse (see docs/openapi.json).
export interface EddCaseSummaryDto {
  caseId: string;
  businessKey: string;
  status: string;
  triggerReason: string;
  assignedAnalyst?: string | null;
  // Nom affichable de l'analyste résolu par le backend (annuaire Keycloak) — à préférer
  // au login brut. Optionnel pour rester rétrocompatible avec un backend non déployé.
  assignedAnalystName?: string | null;
  createdAt: string;
  slaDeadline?: string | null;
  firstName?: string | null;
  customerType?: string | null;
  companyName?: string | null;
  lastName?: string | null;
}

export interface EddCaseDetailDto extends EddCaseSummaryDto {
  submittedL1At?: string | null;
  conclusion?: string | null;
  // Valideurs 4-yeux exposés au détail : login + nom résolu (annuaire Keycloak).
  // Optionnels pour rester rétrocompatible avec un backend non déployé.
  l1ApprovedBy?: string | null;
  l1ApprovedByName?: string | null;
  l2ApprovedBy?: string | null;
  l2ApprovedByName?: string | null;
}

// Mirrors the backend EddDocumentResponse.
export interface EddDocumentDto {
  documentId: string;
  documentType: string;
  originalFilename: string;
  comment?: string | null;
  uploadedBy: string;
  uploadedAt: string;
  contentType?: string | null;
  sizeBytes?: number | null;
}

// Mirrors the backend EddCaseEventResponse (flat audit line — no from/to status).
export interface EddHistoryEntryDto {
  eventType: string;
  actorId: string;
  // Nom affichable de l'acteur résolu par le backend — à préférer à l'UUID/login.
  actorName?: string | null;
  detail?: string | null;
  eventAt: string;
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
export function mapEddStatus(raw: string): EddStatus {
  switch (raw) {
    case "OPEN": return EddStatus.OPEN;
    case "IN_PROGRESS": return EddStatus.IN_PROGRESS;
    case "PENDING_L1": return EddStatus.PENDING_L1;
    case "PENDING_L2": return EddStatus.PENDING_L2;
    case "APPROVED": return EddStatus.APPROVED;
    case "REJECTED": return EddStatus.REJECTED;
    case "SUSPENDED": return EddStatus.SUSPENDED;
    default: return EddStatus.OPEN;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapTrigger(raw: string): EddTriggerReason {
  switch (raw) {
    case "PEP": return EddTriggerReason.PEP;
    case "PEP_WITH_SANCTIONED_COUNTRY": return EddTriggerReason.PEP_WITH_SANCTIONED_COUNTRY;
    case "HIGH_RISK": return EddTriggerReason.HIGH_RISK;
    case "KYB_HIGH_RISK": return EddTriggerReason.KYB_HIGH_RISK;
    case "MANUAL": return EddTriggerReason.MANUAL;
    case "ESCALATION_L1": return EddTriggerReason.ESCALATION_L1;
    case "RE_KYC_TRIGGER": return EddTriggerReason.RE_KYC_TRIGGER;
    case "TRANSACTION_ALERT": return EddTriggerReason.TRANSACTION_ALERT;
    default: return EddTriggerReason.UNKNOWN;
  }
}

// Use explicit branching to avoid dynamic object access patterns flagged by detect-object-injection.
function mapDocType(raw: string): EddDocumentType {
  switch (raw) {
    case "IDENTITY": return EddDocumentType.IDENTITY;
    case "SOURCE_OF_FUNDS": return EddDocumentType.SOURCE_OF_FUNDS;
    case "BUSINESS_ACTIVITY": return EddDocumentType.BUSINESS_ACTIVITY;
    case "BENEFICIAL_OWNER": return EddDocumentType.BENEFICIAL_OWNER;
    default: return EddDocumentType.OTHER;
  }
}

export function mapEddCaseSummaryDto(dto: EddCaseSummaryDto): EddCaseSummary {
  return {
    caseId: dto.caseId,
    businessKey: dto.businessKey,
    status: mapEddStatus(dto.status),
    triggerReason: mapTrigger(dto.triggerReason),
    // Préfère le nom résolu (annuaire) ; retombe sur le login si le backend ne le fournit pas.
    assignedAnalyst: dto.assignedAnalystName ?? dto.assignedAnalyst ?? null,
    createdAt: dto.createdAt,
    slaDeadline: dto.slaDeadline ?? null,
    firstName: dto.firstName ?? null,
    customerType: dto.customerType ?? null,
    companyName: dto.companyName ?? null,
    lastName: dto.lastName ?? null,
  };
}

export function mapEddCaseDetailDto(dto: EddCaseDetailDto): EddCaseDetail {
  return {
    ...mapEddCaseSummaryDto(dto),
    submittedAt: dto.submittedL1At ?? null,
    // Préfère le nom résolu (annuaire) ; retombe sur le login. null tant que non validé.
    l1ApprovedBy: dto.l1ApprovedByName ?? dto.l1ApprovedBy ?? null,
    l2ApprovedBy: dto.l2ApprovedByName ?? dto.l2ApprovedBy ?? null,
    decisionNotes: dto.conclusion ?? null,
  };
}

export function mapEddDocumentDto(dto: EddDocumentDto): EddDocument {
  return {
    documentId: dto.documentId,
    type: mapDocType(dto.documentType),
    fileName: dto.originalFilename,
    comment: dto.comment ?? null,
    uploadedBy: dto.uploadedBy,
    uploadedAt: dto.uploadedAt,
    mimeType: dto.contentType ?? null,
    sizeBytes: dto.sizeBytes ?? null,
  };
}

export function mapEddHistoryDto(dto: EddHistoryEntryDto): EddHistoryEntry {
  return {
    timestamp: dto.eventAt,
    // Préfère le nom résolu (annuaire) ; retombe sur l'UUID/login sinon.
    actor: dto.actorName ?? dto.actorId,
    action: dto.eventType,
    // EddCaseEventResponse is a flat audit line — no status transition fields.
    fromStatus: null,
    toStatus: null,
    note: dto.detail ?? null,
  };
}
